import { omit, upperFirst } from 'lodash-es';
import type { ComponentType, PropsWithChildren, JSX } from 'react';
import { memo, useMemo } from 'react';
import { SortableTHeader } from './sortable-theader';
import type { Sorting } from './sorting';
import { determineNextSorting } from './sorting';
import type { TableProps, THeaderProps } from './table';
import { Table } from './table';

export type THeaders<C extends string> = Record<Capitalize<Lowercase<C>>, ComponentType<THeaderProps>>;

type SortableTableProps<C extends string = string> = Omit<TableProps, 'children'> & {
  columns: C[];
  onSortChange: (sorting: Sorting<C>) => void;
  sorting: Sorting<C>;
  children: (theaders: THeaders<C>) => JSX.Element;
};

export function SortableTable<C extends string>(props: SortableTableProps<C>) {
  const { onSortChange, sorting, columns } = props;

  const theaders = useMemo(
    () => columns.reduce(
      (headers, column) => ({
        ...headers,
        [toComponentName(column)]: getSortableTHeaderComponent(onSortChange, sorting, column),
      }),
      {} as THeaders<C>,
    ),
    [onSortChange, columns, sorting],
  );

  return (
    <Table {...omit(props, 'children', 'columns', 'onSortChange', 'sorting')}>
      {props.children(theaders)}
    </Table>
  );
}

function toComponentName<C extends string>(column: C): Capitalize<Lowercase<C>> {
  return upperFirst(column.toLowerCase()) as Capitalize<Lowercase<C>>;
}

function getSortableTHeaderComponent<C extends string>(onSortChange: (sorting: Sorting<C>) => void, sorting: Sorting<C>, column: C) {
  const onColumnSortChange = (column: C) => onSortChange(determineNextSorting(sorting, column));
  const component = memo((props: PropsWithChildren<THeaderProps>) => (
    <SortableTHeader
      column={column}
      sorting={sorting}
      onColumnSortChange={onColumnSortChange}
      {...props} />
  ));
  return component;
}
